/**
 * Charte graphique du CLIENT (logo, couleurs, police, textes d'en-tête et de pied de page), utilisée
 * dans SES documents (bulletin, attestations, solde de tout compte, factures, rapports…).
 * Elle n'a aucun effet sur l'interface de l'application Nexxus ni sur les gabarits officiels DTS.
 *
 * Stockée dans le champ `branding` du profil entreprise (`Tenant.branding`) : base locale chiffrée en
 * Standalone/Hybride, `tenant_profiles.payload.branding` (PostgreSQL) en Cloud et via la synchronisation.
 */
import { z } from 'zod';
import { brandContrastIssues, isHexColor, normalizeHexColor, type ContrastIssue } from './colors';
import { DEFAULT_FONT_KEY, SAFE_FONT_KEYS, type SafeFontKey } from './fonts';
import {
  LOGO_MAX_BYTES,
  LogoRejectedError,
  base64ToBytes,
  bytesToBase64,
  sanitizeLogoBytes,
  type LogoMime,
} from './logoSanitizer';

export const BRANDING_VERSION = 1 as const;
export const BRANDING_TEXT_MAX = { header_text: 200, footer_text: 400 } as const;
/** Borne de la forme base64 du logo (512 Ko → ~683 Ko) : refus avant tout décodage. */
export const LOGO_BASE64_MAX_LENGTH = Math.ceil(LOGO_MAX_BYTES / 3) * 4;

/** Charte neutre Nexxus actuelle des documents (couleurs d'édition par défaut dans l'écran). */
export const DEFAULT_BRAND_PRIMARY = '#0B2545';
export const DEFAULT_BRAND_SECONDARY = '#1F7A8C';

export const LOGO_POSITIONS = ['left', 'center', 'right'] as const;
export type LogoPosition = (typeof LOGO_POSITIONS)[number];

export interface CompanyBrandingLogo {
  readonly mime: LogoMime;
  /** Octets du logo NETTOYÉ, en base64 strict (jamais d'URL). */
  readonly data: string;
  readonly width: number;
  readonly height: number;
  readonly bytes: number;
}

export interface CompanyBranding {
  readonly version: typeof BRANDING_VERSION;
  readonly logo?: CompanyBrandingLogo;
  readonly logo_position: LogoPosition;
  readonly primary_color: string;
  readonly secondary_color: string;
  readonly font: SafeFontKey;
  readonly header_text?: string;
  readonly footer_text?: string;
}

/** Caractères de contrôle interdits dans les textes libres (hors retour à la ligne et tabulation). */
function hasControlChars(value: string): boolean {
  for (let i = 0; i < value.length; i += 1) {
    const c = value.charCodeAt(i);
    if ((c < 0x20 && c !== 0x0a && c !== 0x0d && c !== 0x09) || c === 0x7f) return true;
  }
  return false;
}

function textField(max: number) {
  return z
    .string()
    .max(max, `${String(max)} caractères maximum`)
    .refine((v) => !hasControlChars(v), 'Caractères de contrôle interdits');
}

const hexField = z.string().transform((v, ctx) => {
  const n = normalizeHexColor(v);
  if (!n) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Couleur invalide : #RRGGBB attendu' });
    return z.NEVER;
  }
  return n;
});

const logoSchema = z
  .object({
    mime: z.enum(['image/png', 'image/jpeg']),
    data: z.string().min(1).max(LOGO_BASE64_MAX_LENGTH, 'Logo trop volumineux : 512 Ko maximum'),
    width: z.number().int().positive(),
    height: z.number().int().positive(),
    bytes: z.number().int().positive(),
  })
  .strict();

/** Schéma structurel (le logo est ensuite revérifié octet par octet par `normalizeCompanyBranding`). */
export const companyBrandingSchema = z
  .object({
    version: z.literal(BRANDING_VERSION),
    logo: logoSchema.optional(),
    logo_position: z.enum(LOGO_POSITIONS).default('left'),
    primary_color: hexField,
    secondary_color: hexField,
    font: z.enum(SAFE_FONT_KEYS).default(DEFAULT_FONT_KEY),
    header_text: textField(BRANDING_TEXT_MAX.header_text).optional(),
    footer_text: textField(BRANDING_TEXT_MAX.footer_text).optional(),
  })
  .strict();

export type BrandingIssueCode = 'schema' | 'logo' | 'contrast_primary' | 'contrast_secondary';

export interface BrandingIssue {
  readonly code: BrandingIssueCode;
  readonly field: string;
  readonly message: string;
}

export type BrandingResult =
  | { readonly ok: true; readonly value: CompanyBranding }
  | { readonly ok: false; readonly issues: readonly BrandingIssue[] };

function contrastMessage(issue: ContrastIssue): BrandingIssue {
  const label = issue.field === 'primary_color' ? 'principale' : 'secondaire';
  return {
    code: issue.field === 'primary_color' ? 'contrast_primary' : 'contrast_secondary',
    field: issue.field,
    message: `Contraste insuffisant : la couleur ${label} (${issue.ratio.toFixed(1)}:1) doit atteindre ${String(issue.required)}:1 avec le texte blanc des en-têtes et pieds de page (WCAG AA). Choisissez une teinte plus foncée.`,
  };
}

/**
 * Valide ET assainit une charte (entrée non fiable : formulaire, import, API, synchronisation).
 * Le logo est entièrement revérifié et RÉÉCRIT (métadonnées retirées) ; ses dimensions et son poids
 * sont recalculés à partir des octets, jamais crus sur parole.
 */
export function normalizeCompanyBranding(raw: unknown): BrandingResult {
  const parsed = companyBrandingSchema.safeParse(raw);
  if (!parsed.success) {
    return {
      ok: false,
      issues: parsed.error.issues.map((i) => ({
        code: 'schema' as const,
        field: i.path.join('.') || 'branding',
        message: i.message,
      })),
    };
  }
  const data = parsed.data;
  const issues: BrandingIssue[] = [];

  let logo: CompanyBrandingLogo | undefined;
  if (data.logo) {
    const bytes = base64ToBytes(data.logo.data);
    if (!bytes) {
      issues.push({ code: 'logo', field: 'logo', message: 'Logo illisible (base64 invalide).' });
    } else {
      try {
        const clean = sanitizeLogoBytes(bytes);
        logo = {
          mime: clean.mime,
          data: bytesToBase64(clean.bytes),
          width: clean.width,
          height: clean.height,
          bytes: clean.bytes.length,
        };
      } catch (err) {
        if (!(err instanceof LogoRejectedError)) throw err;
        issues.push({ code: 'logo', field: 'logo', message: err.message });
      }
    }
  }

  if (isHexColor(data.primary_color) && isHexColor(data.secondary_color)) {
    for (const c of brandContrastIssues(data.primary_color, data.secondary_color)) {
      issues.push(contrastMessage(c));
    }
  }
  if (issues.length > 0) return { ok: false, issues };

  const value: CompanyBranding = {
    version: BRANDING_VERSION,
    ...(logo ? { logo } : {}),
    logo_position: data.logo_position,
    primary_color: data.primary_color,
    secondary_color: data.secondary_color,
    font: data.font,
    ...(data.header_text?.trim() ? { header_text: data.header_text.trim() } : {}),
    ...(data.footer_text?.trim() ? { footer_text: data.footer_text.trim() } : {}),
  };
  return { ok: true, value };
}

/** Charte d'édition « neutre » (point de départ de l'écran ; n'est PAS enregistrée tant que rien n'est modifié). */
export function defaultBrandingDraft(): CompanyBranding {
  return {
    version: BRANDING_VERSION,
    logo_position: 'left',
    primary_color: DEFAULT_BRAND_PRIMARY,
    secondary_color: DEFAULT_BRAND_SECONDARY,
    font: DEFAULT_FONT_KEY,
  };
}

/** Construit une charte à partir d'un fichier téléversé (lève `LogoRejectedError`). */
export function brandingLogoFromBytes(
  bytes: Uint8Array,
  maxBytes: number = LOGO_MAX_BYTES,
): CompanyBrandingLogo {
  const clean = sanitizeLogoBytes(bytes, maxBytes);
  return {
    mime: clean.mime,
    data: bytesToBase64(clean.bytes),
    width: clean.width,
    height: clean.height,
    bytes: clean.bytes.length,
  };
}
